/** tm/api.ts — 타입 안전한 API 래퍼 */
import type {
  ApiResponse, PaginatedCompanies, StepUpdateResult,
  WM, FP, Company, Task, TaskStep, Consultation, Comment,
  GovProgram, GovHiddenKind, GovPreviewResult, GovTargetRow, GovTargetSnap, GovTargetAiResult, GovFixState, GovFixAction, GovFixResult, AIPreset, TaskCategory, TaskTemplate,
  GovFeedStats, GovFeedGroupsPage, GovFeedDetail, GovFeedImportRow, GovFeedBucket, GovFeedBulkApproveResult,
  GovFeedDocItem, GovFeedRule, GovFeedRulesInfo, GovFeedBudget, GovFeedScope, GovFeedScopeMode,
  GovCheckInfo, GovCheckRun, GovCheckRunSummary, GovCheckKind,
} from './types';
import { keepNewToken } from '../tokenRefresh';
import { onAuthLost } from '../sessionGuard';

let BASE = '/api/tm';
let TOKEN_KEY = 'token';

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}
/** 다른 앱에 끼워 쓸 때 주소·토큰 바꾸기 — 올댓벤처스 어드민 「법령·판례·뉴스」 묶음(atv-legal-entry)이 부른다 (2026-10-03).
 *  로움 화면에서는 부르지 않는다(기본값 그대로) */
export function configureApi(o: { base?: string; tokenKey?: string }): void {
  if (o.base) BASE = o.base;
  if (o.tokenKey) TOKEN_KEY = o.tokenKey;
}
export const apiBase = (): string => BASE;
export const apiToken = (): string => getToken();

export async function req<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
  };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);

  const res = await fetch(BASE + path, opts);
  // 304(캐시) 응답의 X-New-Token은 무시 — 캐시된 옛 토큰으로 덮어쓰기 방지
  if (res.status === 200 || res.status === 201) {
    const newToken = res.headers.get('X-New-Token');
    keepNewToken(newToken, TOKEN_KEY);
  }

  // 401 시 에러만 throw (토큰 삭제/리다이렉트 안 함 — 다른 요청에 영향 방지)
  if (res.status === 401) {
    const errData = await res.json().catch(() => ({ error: '인증 오류' }));
    onAuthLost(errData.error || '');   // 토큰이 정말 끝났으면 내 계정 · 로그인으로(막다른 「다시 시도」 대신 · sessionGuard)
    throw new Error(errData.error || '인증 오류');
  }

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `API ${res.status}`);
  return json;
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter((e): e is [string, string | number] => e[1] != null && e[1] !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return parts.length ? '?' + parts.join('&') : '';
}

// ── WMs ──
export const getWms = () => req<WM[]>('GET', '/wms');
export const createWm = (d: Partial<WM>) => req<WM>('POST', '/wms', d);
export const updateWm = (id: string, d: Partial<WM>) => req<WM>('PUT', `/wms/${id}`, d);
/** WM님 대출 요청 링크 만들기(이미 있으면 그대로 · 2026-10-29) */
export const createWmLoanLink = (id: string) => req<WM>('POST', `/wms/${id}/loan-link`);
export const deleteWm = (id: string) => req<{ deleted: boolean }>('DELETE', `/wms/${id}`);

// ── FPs ──
export const getFps = () => req<FP[]>('GET', '/fps');
export const createFp = (d: Partial<FP>) => req<FP>('POST', '/fps', d);
export const updateFp = (id: string, d: Partial<FP>) => req<FP>('PUT', `/fps/${id}`, d);
export const deleteFp = (id: string) => req<{ deleted: boolean }>('DELETE', `/fps/${id}`);
export const generateFpToken = (id: string) => req<FP>('POST', `/fps/${id}/generate-token`);

// ── Companies ──
export const getCompanies = (p?: { search?: string; status?: string; region?: string; biz_type?: string; page?: number; limit?: number }) =>
  req<PaginatedCompanies>('GET', '/companies' + qs(p || {}));
/** 업체 전체 조회 — 서버 페이지네이션(total)을 따라 모든 페이지를 이어 받는다 (500건 상한 제거, R20260910) */
export const getAllCompanies = async (p?: { search?: string; status?: string; region?: string; biz_type?: string }): Promise<ApiResponse<PaginatedCompanies>> => {
  const PAGE = 500;
  const all: Company[] = [];
  let page = 1;
  let total = 0;
  let last: ApiResponse<PaginatedCompanies> | null = null;
  for (;;) {
    const res = await req<PaginatedCompanies>('GET', '/companies' + qs({ ...(p || {}), page, limit: PAGE }));
    last = res;
    const rows = res.data?.companies || [];
    all.push(...rows);
    total = Number(res.data?.total ?? all.length);
    if (rows.length < PAGE || all.length >= total || page >= 200) break;
    page += 1;
  }
  return { ...(last as ApiResponse<PaginatedCompanies>), data: { companies: all, total: Math.max(total, all.length), page: 1, limit: all.length } };
};
/** 업체 한 곳 (2026-10-11) */
export const getCompany = (id: string) => req<Company>('GET', `/companies/${id}`);
/** 업무 진행 규칙 (2026-10-12 · backend services/stepFlow) — 응답 undo 를 taskRestore 로 보내면 되돌리기 */
export type FlowAction = 'done' | 'upto' | 'goto' | 'postpone' | 'due' | 'wait' | 'reply' | 'nudge' | 'fixed' | 'hold' | 'stop' | 'close' | 'retry' | 'ask' | 'finish' | 'restart' | 'trash';   // trash = 화면 쪽 — deleteTask(휴지통)
export interface FlowBody { action: FlowAction; stepId?: string; days?: number; date?: string; shift?: boolean; wait_for?: string[]; question?: string; by?: string; reason?: string; fixed?: boolean; result_word?: string; result_at?: string; exact?: boolean }
export interface FlowUndo { task: { status: string; stop_reason: string | null; stopped_at: string | null }; steps: { id: string; status: string; due: string | null; extras: unknown; sort_order?: number }[]; remove?: string[] }
/** 업무에 아직 안 이어진 이 업체 자료수집 — 업무 등록 때 「기존 자료 요청 잇기」(2026-10-28 · /api/projects/unlinked) */
export interface UnlinkedCollect { id: string; project: string; created_at: string; deadline: string | null; items: number }
export async function unlinkedCollects(companyId: string): Promise<UnlinkedCollect[]> {
  try {
    const r = await fetch(`/api/projects/unlinked/${encodeURIComponent(companyId)}`, { headers: { Authorization: 'Bearer ' + getToken() } });
    const j = await r.json() as { success?: boolean; data?: UnlinkedCollect[] };
    return j.success && Array.isArray(j.data) ? j.data : [];
  } catch { return []; }
}
export const taskFlow = (taskId: string, body: FlowBody) => req<{ message: string; undo: FlowUndo; task: Task }>('POST', `/tasks/${taskId}/flow`, body);
/** 빠진 입력 (2026-10-18 · backend services/inputCheck) — 업무 입력이 빠져 고객 · 팀장님 화면이 틀리게 보이는 줄 */
export type InputRule = 'result' | 'noq' | 'consult' | 'due' | 'nosteps' | 'redo' | 'wait' | 'resdue' | 'stale' | 'loan' | 'upload' | 'notask' | 'tent';
export interface InputCheckItem {
  rule: InputRule; key: string; level: 'must' | 'should'; label: string; title: string; shows: string;
  company_id?: string | null; company: string; task_id?: string | null; task: string; step_id?: string; step?: string; consult_id?: string;
  /** 담당(업무 담당 · 비면 오재인) · 상담 · 가약속은 '' = 공통 */
  who?: string;
  since: string; days: number; again: number; extra?: { judge?: { yes: string; no: string }; who?: string[]; n?: number; project_id?: string; name?: string; project?: string; biz_no?: string; ceo?: string };
}
export interface InputCheck { items: InputCheckItem[]; counts: { must: number; should: number; hidden: number }; byRule: Partial<Record<InputRule, number>>; rules: Record<InputRule, { level: 'must' | 'should'; label: string; title: string; shows: string }>; today: string }
export const getInputCheck = () => req<InputCheck>('GET', '/input-check');
export const markInputCheck = (key: string, kind: 'later' | 'never', reason?: string) => req<{ key: string; kind: string; until: string | null }>('POST', '/input-check/mark', { key, kind, reason });
export const unmarkInputCheck = (key: string) => req<{ removed: boolean }>('DELETE', `/input-check/mark/${encodeURIComponent(key)}`);
export const taskRestore = (taskId: string, undo: FlowUndo) => req<{ message: string; task: Task }>('POST', `/tasks/${taskId}/restore`, undo);
/** 고객 페이지 링크 /c/<token> — reissue 면 새로 만들고 옛 링크는 닫힘 (2026-10-12) */
export const issueClientLink = (companyId: string, reissue = false) => req<{ token: string; path: string }>('POST', `/companies/${companyId}/client-link`, { reissue });
/** 고객 · FP 문의 연락처 — 카카오톡 채널 주소 · 문자 받을 번호 (전화 버튼 없음) */
export interface ClientContact { kakao_url: string; sms_phone: string; morning_sms_phone?: string }
export const getClientContact = () => req<ClientContact>('GET', '/client-contact');
export const putClientContact = (d: ClientContact) => req<ClientContact>('PUT', '/client-contact', d);
/** 업체 저장 — 담당 FP 여러 명(fp_ids) · 담당 WM 여러 명(wm_ids · 첫 분 대표 · 2026-10-26) */
export type CompanyBody = Partial<Company> & { fp_ids?: string[]; wm_ids?: string[] };
export const createCompany = (d: CompanyBody) => req<Company>('POST', '/companies', d);
/** 업체 생성 — 사업자번호/업체명 중복(409) 시 기존 업체 id 반환 (venture/api.ts createCompany와 동일 패턴) */
export const createCompanyOrLink = async (d: CompanyBody): Promise<{ id: string; existed: boolean; existingName?: string }> => {
  const res = await fetch(BASE + '/companies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
    body: JSON.stringify(d),
  });
  if (res.status === 200 || res.status === 201) {
    const nt = res.headers.get('X-New-Token');
    keepNewToken(nt);
  }
  const json = await res.json() as { success?: boolean; data?: { id?: string }; error?: string; existing?: { id?: string; name?: string } };
  if (res.status === 409 && json.existing && json.existing.id) {
    return { id: json.existing.id, existed: true, existingName: json.existing.name };
  }
  if (!res.ok || !json.success || !json.data || !json.data.id) throw new Error(json.error || `API ${res.status}`);
  return { id: json.data.id, existed: false };
};
/** 업체 저장 — 저장되면 「tm:company-saved」 알림 → 업무관리 공용 업체 목록(hooks TMStore)이 그 업체만 다시 읽는다
 *  (2026-10-29 지도사 「상담 결과 등록하면서 체크한 것들이 저장이 안 돼 있어 — 새로고침하니까 저장돼 있다」 — 상담 결과 · 업무 등록 등 여러 화면이 따로 저장하고 목록은 안 바뀌던 것) */
export const COMPANY_SAVED = 'tm:company-saved';
export const updateCompany = async (id: string, d: CompanyBody) => {
  const r = await req<Company>('PUT', `/companies/${id}`, d);
  try { window.dispatchEvent(new CustomEvent(COMPANY_SAVED, { detail: { id } })); } catch { /* 알림만 빠짐 */ }
  return r;
};
export const deleteCompany = (id: string) => req<{ deleted: boolean }>('DELETE', `/companies/${id}`);
// V21_DND:1-C — 업체 순서 일괄 갱신
export const reorderCompanies = (ids: string[]) =>
  req<{ reordered: number }>('POST', '/companies/reorder', { ids });

// ── Tasks ──
export const getTasks = (p?: { company_id?: string; status?: string }) =>
  req<Task[]>('GET', '/tasks' + qs(p || {}));
/** 벤처인증(투자) 업무 — 업무 등록 화면에서 고른 GP 새 조합 · LP 조합 · LP 후보 (2026-10-18 · services/ventureFromTask) */
export interface TaskVentureReq { role: 'gp' | 'lp'; org?: string; project_id?: string; office?: 'roum' | 'atv'; invest_plan?: string }
export const ventureReq = (v?: { role: 'gp' | 'lp'; org?: string; projectId?: string; office?: 'roum' | 'atv'; investPlan?: string }): TaskVentureReq | undefined => (v ? { role: v.role, org: v.org || undefined, project_id: v.projectId || undefined, office: v.office === 'atv' ? 'atv' : 'roum', invest_plan: (v.investPlan || '').trim() || undefined } : undefined);
export const createTask = (d: Omit<Partial<Task>, 'steps'> & { steps?: Partial<TaskStep>[]; venture?: TaskVentureReq }) => req<Task>('POST', '/tasks', d);   // 2026-10-12 — 단계는 일부 칸만 넘겨도 되게(Task.steps 와 겹치던 타입 바로잡음)
export const updateTask = (id: string, d: Partial<Task>) => req<Task>('PUT', `/tasks/${id}`, d);
export const deleteTask = (id: string) => req<{ deleted: boolean }>('DELETE', `/tasks/${id}`);   // 휴지통으로(30일 보관 · 2026-10-12)
export const undeleteTask = (id: string) => req<{ restored: boolean }>('POST', `/tasks/${id}/undelete`);
export const reorderTasks = (company_id: string, task_ids: string[]) =>
  req<{ reordered: number }>('PUT', '/tasks/reorder', { company_id, task_ids });

/** 업무 단계 저장 알림 — 어느 화면에서 단계를 고치든(상담 결과 · 대출 점검 등) 업무관리 공용 목록(hooks TMStore)이 업무를 다시 읽는다
 *  (2026-10-29 지도사 「상담 결과에서 날짜 · 삭제 — 업무관리에도 자동 반영되는 거지?」 — 창을 그냥 닫으면 다시 들어올 때까지 옛 목록이던 것) */
export const TASKS_SAVED = 'tm:tasks-saved';
const tasksSaved = <T,>(p: Promise<T>): Promise<T> => p.then((r) => { try { window.dispatchEvent(new CustomEvent(TASKS_SAVED)); } catch { /* 알림만 빠짐 */ } return r; });
export const updateStep = (taskId: string, stepId: string, d: Partial<TaskStep>) =>
  tasksSaved(req<StepUpdateResult>('PUT', `/tasks/${taskId}/steps/${stepId}`, d));
export const reorderSteps = (taskId: string, stepIds: string[]) =>
  req<{ reordered: boolean }>('PUT', `/tasks/${taskId}/steps/reorder`, { stepIds });
export const bulkUpdateSteps = (taskId: string, d: { steps: Partial<TaskStep>[]; taskData?: Partial<Task> }) =>
  req<Task>('PUT', `/tasks/${taskId}/steps-bulk`, d);
export const addStep = (taskId: string, d: Partial<TaskStep>) =>
  tasksSaved(req<TaskStep>('POST', `/tasks/${taskId}/steps`, d));
export const deleteStep = (taskId: string, stepId: string) =>
  tasksSaved(req<{ deleted: boolean; steps: TaskStep[] }>('DELETE', `/tasks/${taskId}/steps/${stepId}`));

// ── Consultations ──
export const getConsultations = (p?: { month?: string; fp_id?: string }) =>
  req<Consultation[]>('GET', '/consultations' + qs(p || {}));
export const createConsultation = (d: Partial<Consultation>) => req<Consultation>('POST', '/consultations', d);
export const updateConsultation = (id: string, d: Partial<Consultation>) =>
  req<Consultation>('PUT', `/consultations/${id}`, d);
/** 전에 적은 계약 가능성 이유(자동완성 · 2026-10-29) */
export const getDealReasons = () => req<string[]>('GET', '/consultations/deal-reasons');
export const deleteConsultation = (id: string) =>
  req<{ deleted: boolean }>('DELETE', `/consultations/${id}`);
/** 상담 결과 보내기 — 받는 분(담당 FP · 함께한 WM) · 처음 본문 (2026-10-27) */
/** 녹음 정리되면 보내기(2026-10-29 · 서버 consultSend.sendWhenDone) — pending = 걸어 둠 · sending = 보내는 중 · sent/partial/failed = 정리 뒤 보낸 결과 */
export interface SendPending { state: 'pending' | 'sending' | 'sent' | 'partial' | 'failed'; keys: string[]; at: string | null; results: { key: string; name: string; sent: boolean; error?: string }[]; error: string }
/** 저녁 6시 뒤 결과 보내기 — WM 님 몫은 다음 날 오전 9시(2026-10-29 · 서버 consultSend.dispatch) · due = 보낼 때 */
export interface SendMorning { state: 'pending' | 'sending' | 'sent' | 'partial' | 'failed'; keys: string[]; due: string | null; results: { key: string; name: string; sent: boolean; error?: string }[]; error: string; sent_at: string | null }
export interface SendTarget { key: string; role: 'FP' | 'WM'; name: string; honor: string; phone: string; why: string }   // honor = 「김인철 WM님」(서버 consultSend.honorOf)
/** 결과 보내기 기록 — 보낸 차례별 · 받는 분마다 받음 · 보내는 중 · 못 받음 · 못 보냄(2026-10-29) */
export interface SendLogRound { at: string; items: { name: string; role: string; state: 'ok' | 'wait' | 'fail' | 'rejected'; note: string }[] }
export const getConsultSendLog = (id: string) => req<SendLogRound[]>('GET', `/consultations/${id}/send-log`);
export const getConsultSendTargets = (id: string) =>
  req<{ title: string; body: string; recipients: SendTarget[]; forward?: { report: boolean; collect: boolean; client: boolean }; waiting?: number; pending?: SendPending | null; morning?: SendMorning | null; night?: boolean; morningDue?: string }>('GET', `/consultations/${id}/send-targets`);   // forward = 대표님께 전달할 것(2026-10-28)
/** 골라서 한 번에 — 받는 분마다 결과 */
export const sendConsultResult = (id: string, body: string, keys: string[], later = false) =>
  req<{ results: { key: string; name: string; sent: boolean; error?: string }[]; pending?: boolean; now?: boolean; morning?: { keys: string[]; due: string } }>('POST', `/consultations/${id}/send-result`, { body, keys, later });   // later = 녹음 정리되면(정리 중인 녹음이 없으면 바로 · 2026-10-29)
export const cancelConsultSend = (id: string) => req<{ cancelled: boolean }>('POST', `/consultations/${id}/send-cancel`, {});
/** 아침에 보낼 WM 님 몫 — 취소 · 지금 보내기 */
export const cancelConsultMorning = (id: string) => req<{ cancelled: boolean }>('POST', `/consultations/${id}/send-morning/cancel`, {});
export const sendConsultMorningNow = (id: string) => req<{ state: string; results: { key: string; name: string; sent: boolean; error?: string }[] }>('POST', `/consultations/${id}/send-morning/now`, {});

// consultation file upload/delete are handled via fetch in ConsultResultModal directly

// ── Comments ──
export const getComments = (companyId: string) =>
  req<Comment[]>('GET', `/comments?company_id=${companyId}`);
export const createComment = (d: Partial<Comment>) => req<Comment>('POST', '/comments', d);
export const updateComment = (id: string, d: Partial<Comment>) =>
  req<Comment>('PUT', `/comments/${id}`, d);
export const deleteComment = (id: string) =>
  req<{ deleted: boolean }>('DELETE', `/comments/${id}`);

// ── Gov Programs ──
// ── 월간 소식지 «담을 범위» (2026-10-09 · backend routes/tm-newsletter.js) ──
export type NewsletterIssueKind = '세법' | '법령' | '제도' | '판례' | '유권해석' | '정책';
export interface NewsletterIssue {
  id: string; kind: NewsletterIssueKind;
  /** 출처 한 줄 — 「법령 · 2026 세법개정안 · 9월 국회 제출」 */ label: string;
  /** 이슈 칸 머리 오른쪽 작은 글 — 「세법개정안」 */ caption: string;
  /** 제목(줄바꿈 \n 로 2줄까지) */ title: string;
  body: string; /** 행동 지침 한 줄 */ action: string; link: string;
  /** 기본 이슈 — 사용자가 따로 고르지 않을 때 */ star: boolean;
  /** 체크 해제 = 세무사 · fphub 소식지 만들기에 안 보임(보관만 · 2026-10-10) */ off?: boolean;
}
/** 소식지에서만 쓰는 직접 등록 지원사업 (2026-10-10 지도사) — id 'nl-…' · 정부지원사업 탭 · 리포트 · 매칭에 들어가지 않는다 */
export interface NewsletterCustom {
  id: string; name: string; agency: string; amount: string; category: string;
  /** 지역 — areas { 시도: [시군구] } (빈 객체 = 전국 · 시군구 빈 배열 = 그 시도 전체) · region/region_detail 은 서버가 areas 로 만든 글 */
  areas?: Record<string, string[]>; region: string; region_detail?: string;
  /** 기업 규모(빈 = 규모 무관) · 업종(소식지 만들기 5가지 · 빈 = 업종 무관) — 2026-10-10 */
  sizes?: string[]; biz?: string[];
  apply_start: string | null; deadline: string | null; first_come: boolean; link: string; created_at?: string;
}
export interface NewsletterMonth { ym: string; exists: boolean; program_ids: string[]; issues: NewsletterIssue[]; customs?: NewsletterCustom[]; published: boolean; memo: string; updated_at: string | null }
export interface NewsletterMonthSummary { ym: string; n_programs: number; n_issues: number; published: boolean; updated_at: string }
export interface NewsletterUsageRow { id: string; site: 'tax' | 'fphub' | 'atv'; ym: string; office: string; person: string; kind: 'pdf' | 'jpg'; user_ref: string; created_at: string }
export const getNewsletterUsage = (ym?: string) => req<NewsletterUsageRow[]>('GET', '/newsletter/usage' + qs(ym ? { ym } : {}));
export const getNewsletterMonths = () => req<NewsletterMonthSummary[]>('GET', '/newsletter');
export const getNewsletter = (ym: string) => req<NewsletterMonth>('GET', `/newsletter/${ym}`);
/** 이달의 이슈 추천 — 「법령·판례·뉴스」 목록에서(AI 없음) · draft 는 이슈 칸에 그대로 채운다 (2026-10-09) */
export interface NewsletterIdea { ref: { kind: string; tab: string; id: number }; score: number; why: string[]; date: string; starred: boolean; draft: Omit<NewsletterIssue, 'id' | 'star'> }
/** 그 해 공고가 아닌(지난해) 활성 공고 [{id, year}] — 후보에서 뺀다 (2026-10-09) */
export const getNewsletterStale = (ym: string) => req<{ id: string; year: number | null }[]>('GET', `/newsletter/${ym}/stale`);
export const getNewsletterIdeas = (ym: string) => req<NewsletterIdea[]>('GET', `/newsletter/${ym}/ideas`);
export const saveNewsletter = (ym: string, d: Partial<Pick<NewsletterMonth, 'program_ids' | 'issues' | 'customs' | 'published' | 'memo'>>) => req<NewsletterMonth>('PUT', `/newsletter/${ym}`, d);

export const getGovPrograms = (p?: { include_inactive?: boolean }) =>
  req<GovProgram[]>('GET', '/gov-programs' + qs(p?.include_inactive ? { include_inactive: 1 } : {}));
/** 등록/수정 — 같은 이름+기관의 활성 공고가 이미 있으면 막지 않고 warning:'duplicate' 로 알린다(감사 A-9) */
export interface GovProgramSaveResult extends ApiResponse<GovProgram> { warning?: 'duplicate' }
export const createGovProgram = (d: Partial<GovProgram>): Promise<GovProgramSaveResult> => req<GovProgram>('POST', '/gov-programs', d);
/** 공고명·FP 사업명 → 리포트 사업명(공식 사업명만) — 서버 규칙(programName.reportNameFrom) 한 벌 (2026-10-01) */
export const govReportNameFrom = (title: string) => req<{ report_name: string }>('POST', '/gov-programs/report-name', { title });
export const updateGovProgram = (id: string, d: Partial<GovProgram>): Promise<GovProgramSaveResult> =>
  req<GovProgram>('PUT', `/gov-programs/${id}`, d);
/** 저장 전 미리보기 (2026-10-05) — 바디 = 저장 바디와 같은 모양. 샘플 4곳 판정 + 내 업체 해당 건수. tracks·conditions 가 틀리면 400(저장과 같은 메시지) */
export const previewGovProgram = (body: Partial<GovProgram>) => req<GovPreviewResult>('POST', '/gov-programs/preview', body);
/** 하드 삭제 대신 is_active=false 소프트 삭제(감사 A-10) — 목록에서는 사라지고 「비활성 보기」에서 보인다 */
export const deleteGovProgram = (id: string) => req<{ deleted: boolean; deactivated: boolean }>('DELETE', `/gov-programs/${id}`);
/** 같은 공고 묶음 손 조작 (2026-10-05 · 148) — dup_off 「같은 공고 아님」 · hide_off 「보이기」 · pin 「묶기」(대표 id · null = 손 묶음 풀기). 다시 묶은 결과를 돌려준다 */
export interface GovDupMember { id: string; name: string; agency: string; region: string; region_detail?: string | null; apply_start: string | null; deadline: string | null; dup_primary: boolean; is_active: boolean }
export interface GovDupResult { row: GovProgram; group: string | null; size: number; hidden_kind: GovHiddenKind | null; dup_review: boolean; dup_review_of: string | null; members: GovDupMember[] }
export const setGovDup = (id: string, body: { dup_off?: boolean; hide_off?: boolean; pin?: string | null }) => req<GovDupResult>('PUT', `/gov-programs/${id}/dup`, body);
/* 「공고 고치기」 (2026-10-07g · routes/tm-gov.js) — 같은 사업 묶음 풀기 · 묶기 · 리포트 칸 · 짧은 이름 · 매칭 조건. 리포트(fphub 관리자) 패널과 같은 서비스(reportFix) */
export const getGovFix = (id: string) => req<GovFixState>('GET', `/gov-programs/${id}/fix`);
export const postGovFix = (id: string, body: GovFixAction) => req<GovFixResult>('POST', `/gov-programs/${id}/fix`, body);

// ── 공고 검토함 (기업마당 → AI 조건 분석 → 검토·승인) — routes/tm-gov-feed.js ──
export const getGovFeedStats = () => req<GovFeedStats>('GET', '/gov-feed/stats');
export interface GovFeedGroupsQuery {
  review?: 'new' | 'approved' | 'excluded' | 'all'; screen?: 'fit' | 'maybe' | 'unfit' | 'pending' | 'all';
  field?: string; q?: string; recur?: boolean; dup?: boolean; ai?: 'done' | 'error' | ''; page?: number; size?: number;
  /** 대량 검토 탭 · 지역(광역) · 출처 — 서버가 모르면 무시된다 */
  bucket?: GovFeedBucket; region?: string; source?: string;
  /** 2026-10-03 — 지역 여러 개('전국' = 지역 지정 없는 공고) · nat 전국 공고 포함 · 시군구 · 우리 분류(대분류는 세부까지) · 정렬 · 건수 */
  regions?: string[]; nat?: boolean; cities?: string[]; cats?: string[]; sort?: string; dir?: 'asc' | 'desc'; counts?: boolean;
  /** 관심(별표)만 · 'none' = 별표 안 한 것만(분석 대기 「고르기」 목록 — 2026-10-04) */
  star?: boolean | 'none';
}
export const getGovFeedGroups = (p: GovFeedGroupsQuery) =>
  req<GovFeedGroupsPage>('GET', '/gov-feed/groups' + qs({
    review: p.review, screen: p.screen, field: p.field, q: p.q, recur: p.recur ? 1 : undefined, dup: p.dup ? 1 : undefined,
    ai: p.ai || undefined, bucket: p.bucket, region: p.region, source: p.source, page: p.page, size: p.size,
    regions: p.regions && p.regions.length ? p.regions.join(',') : undefined, nat: p.nat ? 1 : undefined,
    cities: p.cities && p.cities.length ? p.cities.join(',') : undefined, cats: p.cats && p.cats.length ? p.cats.join(',') : undefined,
    sort: p.sort || undefined, dir: p.sort ? p.dir : undefined, counts: p.counts ? 1 : undefined, star: p.star === 'none' ? '0' : p.star ? 1 : undefined,
  }));
/** 같은 공고 묶음에서 하나 빼기 (따로 보기) */
export const splitGovFeed = (repId: string, childId: string) => req<{ id: string }>('POST', `/gov-feed/groups/${repId}/split`, { childId });
/** 관심(별표)만 다시 분석 — dry: 건수·예상 비용만 · 아니면 고친 값 학습 → 대기열 (2026-10-03) */
export const reanalyzeStarredGovFeed = (dry: boolean) =>
  req<{ matched: number; queued?: number; skipped?: number; maxWon?: number | null; remaining: number; started?: boolean; learning?: boolean }>('POST', '/gov-feed/reanalyze-open', { starred: true, dry });
/** 관심 별표 켜기·끄기 (여러 건) */
export const starGovFeed = (ids: string[], on: boolean) => req<{ updated: number; on: boolean; dequeued?: number }>('POST', '/gov-feed/star', { ids, on });
export const getGovFeedGroup = (id: string) => req<GovFeedDetail>('GET', `/gov-feed/groups/${id}`);
export const importGovFeed = (rows: GovFeedImportRow[], last: boolean) =>
  req<{ inserted: number; updated: number; skipped: number; groups: number; pipelineStarted?: boolean }>('POST', '/gov-feed/import', { rows, last });
export const syncGovFeed = () =>
  req<{ started: boolean; alreadyRunning: boolean; sources?: Record<string, 'started' | 'running' | 'nokey'> }>('POST', '/gov-feed/sync', {});
export const runGovFeedPipeline = (retryErrors?: boolean) =>
  req<{ started: boolean; alreadyRunning: boolean }>('POST', '/gov-feed/pipeline', retryErrors ? { retryErrors: true } : {});
export interface GovFeedApproveResult { program: GovProgram; warning?: 'duplicate' }
export const approveGovFeed = (id: string, body: { program: Partial<GovProgram>; mode: 'new' | 'update'; programId?: string }) =>
  req<GovFeedApproveResult>('POST', `/gov-feed/groups/${id}/approve`, body);
export const excludeGovFeed = (id: string, reason?: string) =>
  req<{ id: string; review: 'excluded' }>('POST', `/gov-feed/groups/${id}/exclude`, reason ? { reason } : {});
export const restoreGovFeed = (id: string) => req<{ id: string; review: 'new' }>('POST', `/gov-feed/groups/${id}/restore`, {});
export const bulkGovFeed = (ids: string[], action: 'exclude' | 'restore') =>
  req<{ updated: number; skipped: number }>('POST', '/gov-feed/bulk', { ids, action });

/** 표·패널에서 고친 등록값(부분 payload) 병합 저장 → 병합된 draft — 승인/제외된 건은 409 */
export const saveGovFeedDraft = (id: string, program: Partial<GovProgram>) =>
  req<{ draft: Partial<GovProgram> | null }>('PUT', `/gov-feed/groups/${id}/draft`, { program });
/** 선택 일괄 등록 (최대 500건/회) — 건별 결과, 한 건 실패가 다른 건을 막지 않는다 */
export const bulkApproveGovFeed = (ids: string[]) =>
  req<GovFeedBulkApproveResult>('POST', '/gov-feed/bulk-approve', { ids });

// ── 원문 보기 ──
/** 등록된 공고 → 그 공고를 만든 검토함 공고(공고문 보기용, 2026-10-03) — 없으면 groupId null */
export const getGovFeedByProgram = (programId: string) => req<{ groupId: string | null; title: string | null }>('GET', `/gov-feed/by-program/${programId}`);
export const getGovFeedDocs = (id: string) => req<{ items: GovFeedDocItem[] }>('GET', `/gov-feed/groups/${id}/doc`);
/** iframe·img 용 «원문 보기 전용» 토큰 (30분) — 만료 1분 전까지 재사용 */
let docTok: { token: string; param: string; until: number } | null = null;
export async function govFeedDocSrc(viewUrl: string): Promise<string> {
  if (!docTok || Date.now() > docTok.until) {
    const r = await req<{ token: string; expiresIn: number; param: string }>('GET', '/gov-feed/doc-token');
    if (!r.data) throw new Error('원문 보기 토큰을 받지 못했습니다');
    docTok = { token: r.data.token, param: r.data.param || 't', until: Date.now() + Math.max(60, r.data.expiresIn - 60) * 1000 };
  }
  return `${viewUrl}${viewUrl.includes('?') ? '&' : '?'}${docTok.param}=${encodeURIComponent(docTok.token)}`;
}
/** 헤더 인증으로 받아 blob URL (개발 서버처럼 다른 출처일 때) */
/** 원문 오류 — 서버가 준 이유를 함께 (2026-10-03: 예전엔 「원문을 열 수 없습니다 (500)」만) */
async function docError(res: Response): Promise<Error> {
  let why = '';
  try { const j: unknown = await res.json(); if (j && typeof j === 'object' && typeof (j as { error?: unknown }).error === 'string') why = (j as { error: string }).error; } catch { /* 본문 없음 */ }
  return new Error(why ? `원문을 열 수 없습니다 — ${why}` : `원문을 열 수 없습니다 (${res.status})`);
}
export async function govFeedDocBlob(viewUrl: string): Promise<string> {
  const res = await fetch(viewUrl, { headers: { 'Authorization': 'Bearer ' + getToken() } });
  if (!res.ok) throw await docError(res);
  return URL.createObjectURL(await res.blob());
}
/** PDF 바이트 (pdf.js 로 그림) — 헤더 인증 */
export async function govFeedDocBytes(viewUrl: string): Promise<ArrayBuffer> {
  const res = await fetch(viewUrl, { headers: { 'Authorization': 'Bearer ' + getToken() } });
  if (!res.ok) throw await docError(res);
  return res.arrayBuffer();
}
/** 이전 회차처럼 text 가 비어 온 글 문서 */
export async function govFeedDocText(viewUrl: string): Promise<string> {
  const res = await fetch(viewUrl, { headers: { 'Authorization': 'Bearer ' + getToken() } });
  if (!res.ok) throw await docError(res);
  return res.text();
}

// ── 총예산 (절대 초과 금지) ──
export const getGovFeedBudget = () => req<GovFeedBudget>('GET', '/gov-feed/budget');
export const setGovFeedBudget = (body: { krw?: number; rate?: number }) => req<GovFeedBudget>('PUT', '/gov-feed/budget', body);
/** 분석 범위 — false: 서울·경기·인천·전국만 · true: 지방까지 (2026-10-03) */
export const setGovFeedScope = (mode: GovFeedScopeMode) => req<GovFeedScope>('PUT', '/gov-feed/scope', { mode });

// ── 교정 학습 ──
export const getGovFeedRules = () => req<GovFeedRulesInfo>('GET', '/gov-feed/rules');
export const createGovFeedRule = (text: string, field?: string | null) => req<{ rule: GovFeedRule }>('POST', '/gov-feed/rules', { text, field: field || null });
export const updateGovFeedRule = (id: number, patch: { text?: string; field?: string | null; enabled?: boolean }) =>
  req<{ rule: GovFeedRule }>('PUT', `/gov-feed/rules/${id}`, patch);
export const deleteGovFeedRule = (id: number) => req<{ id: number; deleted: boolean }>('DELETE', `/gov-feed/rules/${id}`);
export const learnGovFeedRules = () =>
  req<{ rules?: GovFeedRule[]; feedbackUsed?: number; kept?: number; learned?: number; background?: boolean }>('POST', '/gov-feed/rules/learn', {});
export const reanalyzeOpenGovFeed = (buckets: GovFeedBucket[]) =>
  req<{ matched: number; queued: number; capped: boolean; remaining: number; started: boolean; pickOnly?: boolean }>('POST', '/gov-feed/reanalyze-open', { buckets });

/** 이 건만 다시 분석 — 상태 코드가 의미를 가진다(200 완료 · 504 아직 도는 중 · 502 실패 + 상세) 그래서 req() 대신 직접 읽는다 */
export type GovFeedAnalyzeResult =
  | { status: 'done'; detail: GovFeedDetail }
  /** 일괄(Batch) 대기열에 넣음 — already 면 이미 일괄 처리 중 */
  | { status: 'queued'; detail: GovFeedDetail; already: string | null }
  | { status: 'timeout'; error: string }
  | { status: 'failed'; error: string; detail: GovFeedDetail | null }
  | { status: 'budget'; error: string }
  | { status: 'error'; error: string };
/** mode: 'batch'(기본 · 50% 할인 · 보통 1시간 안에) | 'now'(바로 · 정가 · 1분 안팎) — 2026-10-03 */
export async function analyzeGovFeed(id: string, mode: 'batch' | 'now' = 'batch'): Promise<GovFeedAnalyzeResult> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/gov-feed/groups/${id}/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
      body: JSON.stringify({ mode }),
    });
  } catch (e) {
    return { status: 'error', error: e instanceof Error ? e.message : '네트워크 오류' };
  }
  if (res.status === 200 || res.status === 201) {
    const newToken = res.headers.get('X-New-Token');
    keepNewToken(newToken, TOKEN_KEY);
  }
  type Queued = { queued: boolean; already?: string; detail: GovFeedDetail };
  const json = await res.json().catch(() => ({} as { error?: string; data?: GovFeedDetail | Queued }));
  const data = json.data;
  if (res.ok && data && 'queued' in data) return { status: 'queued', detail: data.detail, already: data.already || null };
  if (res.ok && data) return { status: 'done', detail: data as GovFeedDetail };
  if (res.status === 504) return { status: 'timeout', error: json.error || '분석이 오래 걸려 백그라운드에서 계속합니다' };
  if (res.status === 402) return { status: 'budget', error: json.error || 'AI 예산 한도' };
  if (res.status === 502) return { status: 'failed', error: json.error || 'AI 분석 실패', detail: (data && !('queued' in data) ? data : null) };
  return { status: 'error', error: json.error || `API ${res.status}` };
}

/* 정부지원사업 매칭 — 판정은 서버(services/govMatch)에서 한다.
   near = 어기는 조건은 없는데 업체 칸이 비어 확인 불가 (화면에서 「확인 필요」로 분리) */
export interface GovMatchCompany {
  id: string; name: string; ceo: string; ceo_phone: string; biz_no: string;
  /** 실제 대표(있으면 문자는 이 사람에게 · 2026-10-18) */
  real_ceo?: string; real_ceo_phone?: string;
  region: string; region_detail: string; biz_type: string; company_scale: string;
  is_startup: boolean; tm_status: string; ceo_age: number | null;
  fps: { fp_id: string; name: string; phone: string }[];
  why: { k: string; t: string }[];
  missing?: string[];
}
export interface GovMatchResult {
  program: { id: string; name: string; deadline: string | null; category: string };
  /** 엄격 모드 표기 — 발송 대상(pass)은 「전제로 통과」 가 없는 정확히 일치만 (지도사 2026-09-25) */
  strict: boolean;
  pass: GovMatchCompany[];
  near: GovMatchCompany[];
  counts: { pass: number; near: number; fail: number; total: number };
  blockers: Record<string, number>;
  quality: { total: number; missing: Record<string, number> };
}
/** 업종·이름 글 → 자동으로 붙는 전용분야·기업 유형 (계산만 · 2026-10-03) */
export const getGovAutoTags = (body: { industry?: string; product?: string; biz_types?: string[]; name?: string; entity?: string; gov_ai?: unknown }) =>
  req<{ subs?: string[]; arts: string[]; types: string[]; typesQ?: string[]; src?: 'ai' | 'text'; stale?: boolean; ai?: { at?: string | null; conf: 'high' | 'mid' | 'low'; ev: Record<string, string>; fields: string[]; biz: string[]; note?: string } }>('POST', '/gov-match/auto-tags', body);   // subs = 하위 업종 키 (2026-10-05g) · src 'ai' = 저장된 AI 판정이 지금 글과 같음 (2026-10-07k)
/** 업체 세부업종 AI 판정 다시 (2026-10-07k) — 저장(companies.gov_ai)까지 · 답이 오면 상위가 업체를 새로고침 */
export const judgeCompanyGovAi = (id: string) => req<{ gov_ai: unknown; changed: boolean; skipped: string | null }>('POST', `/companies/${id}/gov-ai`, { force: true });
/* 전용 대상 자동 체크 검토 (2026-10-05f) — routes/tm-gov.js */
export interface GovTargetReview { status: 'pending' | 'done'; rows: GovTargetRow[]; counts: { pending: number; done: number } }
export const getGovTargetReview = (status: 'pending' | 'done' = 'pending') => req<GovTargetReview>('GET', '/gov-targets/review' + qs({ status }));
export const getGovTargetPendingCount = () => req<{ pending: number }>('GET', '/gov-targets/review' + qs({ count: 1 }));
/** 검토 대기 일괄 확인 (2026-10-07) — 직접 값과 다름 · 확신 낮음은 남기고 나머지를 「본 것」으로 · 묶음째 되돌리기 */
export interface GovTargetBulkPlan { confirm: number; keep: { diff: number; low: number }; last: { batch: string; n: number } | null }
export const getGovTargetBulk = () => req<GovTargetBulkPlan>('GET', '/gov-targets/review/bulk');
export const runGovTargetBulk = () => req<{ batch: string; confirmed: number }>('POST', '/gov-targets/review/bulk');
export const undoGovTargetBulk = (batch: string) => req<{ restored: number }>('POST', '/gov-targets/review/bulk-undo', { batch });
export type GovTargetAction = { action: 'confirm' | 'none' } | { action: 'exclude' | 'include'; k: string } | { action: 'restore'; snap: GovTargetSnap };
export const reviewGovTarget = (id: string, body: GovTargetAction) => req<{ row: GovTargetRow; snap: GovTargetSnap }>('POST', `/gov-targets/${id}`, body);
/** 「AI 다시 읽기」(2026-10-05o) — 공고문 전체를 AI 가 읽어 하위 업종 · 전용 대상을 다시 판정 · 오래 걸리면 { background:true } */
export const aiRereadGovTarget = (id: string) => req<GovTargetAiResult | { background: true }>('POST', `/gov-targets/${id}/ai`);
// 「AI 판정 점검」 (2026-10-08) — 시험 세트 · 무작위 표본(저장 안 함) · 뒤에서 돌고 화면은 3초마다 진행을 본다
/** 검수용 내려받기 (2026-10-09) — 활성 공고 전부 · 지금 판정 + 본문 조각(jsonl.gz) → 브라우저 파일 저장 */
export async function downloadGovReviewExport(): Promise<{ count: number; filename: string }> {
  // 서버가 뒤에서 만드는 동안(202) 5초마다 다시 묻는다 — 최대 6분
  let res: Response | null = null;
  for (let i = 0; i < 72; i++) {
    res = await fetch(BASE + '/gov-targets-export' + (i === 0 ? '?fresh=1' : ''), { headers: { 'Authorization': 'Bearer ' + getToken() } });
    if (res.status !== 202) break;
    await new Promise((r) => setTimeout(r, 5000));
  }
  if (!res || res.status === 202) throw new Error('내려받기 파일이 아직 만들어지지 않았습니다 — 잠시 뒤 다시 눌러 주세요');
  if (!res.ok) {
    let msg = `내려받기 실패 (${res.status})`;
    try { const j = await res.json() as { error?: string }; if (j && j.error) msg = j.error; } catch (e) { /* 글이 아니면 기본 문구 */ }
    throw new Error(msg);
  }
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : 'gov-review.jsonl.gz';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return { count: Number(res.headers.get('X-Export-Count') || 0), filename };
}
export const getGovCheckInfo = () => req<GovCheckInfo>('GET', '/gov-targets-check');
export const getGovCheckRun = (id: string) => req<GovCheckRun>('GET', `/gov-targets-check/${encodeURIComponent(id)}`);
export const startGovCheck = (body: { kind: GovCheckKind; n?: number; ids?: string[] }) => req<{ id: string; run: GovCheckRunSummary }>('POST', '/gov-targets-check', body);
/* 단톡방 공유 (2026-10-04) */
export interface GovShareItem {
  id: string; name: string; title: string; target: string; period: string; url: string; hasShort: boolean;
  region: string; regionDetail: string; deadline: string | null; expired: boolean; national: boolean; rooms: string[]; daysLeft: number | null; line: string;
  collectedAt: string | null; collectedOn: string | null; sharedIn: Record<string, string>;
  /** 같은 공고 N건 묶음 (2026-10-05 · 148) — 대표 한 줄로 나간다. 1 = 묶음 없음 · 옛 서버 응답엔 없다 */
  dupCount?: number;
}
export interface GovShareRoom {
  key: string; name: string; kakao?: string; regions: string[]; gg: string[];
  items: string[]; shared: string[]; skipped: string[]; changed: string[]; removed?: string[];
  lastShared: string | null; lastBy: string; lastBatch: { id: string; n: number; at: string } | null;
}
export interface GovShareData { today: string; days: number; from: string; asOf: string; header: string; headerTpl: string; rooms: GovShareRoom[]; items: GovShareItem[]; unassigned: string[]; total: number; pending?: number }
export const getGovShare = (days: number) => req<GovShareData>('GET', `/gov-share?days=${days}`);
export const logGovShare = (room: string, ids: string[], lines: Record<string, string>, asOf: string | null) =>
  req<{ batch: string | null; n: number; conflicts: { id: string; by: string; at: string }[] }>('POST', '/gov-share/log', { room, ids, lines, asOf });
export const undoGovShare = (room: string, batch: string) => req<{ n: number }>('POST', '/gov-share/undo', { room, batch });
/** 이 방에서 빼기 · undo=true 면 되살리기 (2026-10-04) */
export const skipGovShare = (room: string, ids: string[], undo = false) => req<{ n: number }>('POST', '/gov-share/skip', { room, ids, undo });
export const saveGovShareSettings = (body: { header?: string; rooms?: Pick<GovShareRoom, 'key' | 'name' | 'kakao' | 'regions' | 'gg'>[] }) => req<{ header: string; rooms: GovShareRoom[] }>('PUT', '/gov-share/settings', body);
export const fillGovShareTargets = (ids: string[], overwrite = false) => req<{ filled: number }>('POST', '/gov-share/targets', { ids, overwrite });
export const setGovShareTarget = (id: string, target: string) => req<{ target: string }>('PUT', `/gov-programs/${id}/share-target`, { target });
export const getGovMatches = (programId: string) =>
  req<GovMatchResult>('GET', `/gov-programs/${programId}/matches`);
/** 업체 → 추천 공고 한 줄 — 공고 원본(gov_programs 행) + 판정 이유 · near 는 비어서 판정 못 한 업체 칸(missing) */
export type GovCompanyMatch = GovProgram & { why?: { k: string; t: string }[]; missing?: string[] };
export interface GovCompanyMatches { pass: GovCompanyMatch[]; near: GovCompanyMatch[]; counts: { pass: number; near: number; fail: number; total: number }; missing: string[] }
export const getCompanyGovMatches = (companyId: string) =>
  req<GovCompanyMatches>('GET', `/companies/${companyId}/gov-matches`);
/** 업체 정보 결측률(엔진 기준 6항목) — CompanyListPage 상단 요약 보조 지표. 매칭 정보 N/9 배지·필터는
 *  별도로 클라이언트에서 계산한다(9항목 = 직원수·매출·설립일·창업여부 포함, 목록 API 재호출 없이) */
// ── 공고 나오면 알림 (2026-10-30 · routes/tm-gov-watch) ──
/** 문자 한 분 — state: pending 보낼 때를 기다림 · sending · sent 보냄 · failed 못 보냄 · skip 안 보냄(번호 없음 등) */
export interface GovWatchSmsOne { key: string; name: string; state: 'pending' | 'sending' | 'sent' | 'failed' | 'skip'; error?: string; at?: string }
/** 문자 묶음 — state: pending · sending · sent · partial · failed · skip · merged(3일 안 같은 공고 · 안 보냄) · off(받을 분 없음) · cancelled(알림을 끝내서) */
export interface GovWatchSms { state: string; due?: string; list: GovWatchSmsOne[]; of?: string }
export interface GovWatchHit {
  id: string; watch_id: string; item_id: string | null; title: string; url: string | null; agency: string;
  apply_start: string | null; apply_end: string | null; apply_text: string | null; registered_at: string | null; period: string;
  kind: 'notice' | 'noise'; noise_why: string; sms: GovWatchSms | null; seen_at: string | null; seen_by: string; created_at: string;
}
export interface GovWatch {
  id: string; company_id: string; name: string; keys: string[]; group_ids: string[]; program_ids: string[]; target_year: number | null; memo: string;
  notify_me: boolean; notify_fp: boolean; status: 'on' | 'off' | 'done'; since: string; created_by_name: string; task_id: string | null;
  ended_at: string | null; last_hit_at: string | null; created_at: string; owner_phone?: boolean; hits?: GovWatchHit[]; merged?: boolean;
}
export interface GovWatchList { ready: boolean; watches: GovWatch[]; fps: { id: string; name: string; has_phone: boolean }[]; my_phone: boolean }
export interface GovWatchSuggest { label: string; agency: string; keys: string[]; program_ids: string[]; group_ids: string[]; latest: string }
export interface GovWatchPreview { id: string; title: string; agency: string; url: string | null; registered_at: string; apply_end: string | null; noise: string; open: boolean; old: boolean }
export interface GovWatchAlert extends GovWatchHit { watch_name: string; memo: string; watch_status: string; company_id: string; company_name: string | null; more: number }
export type GovWatchInput = Pick<GovWatch, 'name' | 'keys' | 'group_ids' | 'program_ids' | 'target_year' | 'memo' | 'notify_me' | 'notify_fp'>;
export const getGovWatches = (companyId: string) => req<GovWatchList>('GET', `/gov-watch?company_id=${encodeURIComponent(companyId)}`);
export const getGovWatchAlerts = () => req<{ ready: boolean; items: GovWatchAlert[]; waiting: number }>('GET', '/gov-watch/alerts');
export const suggestGovWatch = (q: { q?: string; program_id?: string }) =>
  req<GovWatchSuggest[]>('GET', `/gov-watch/suggest?${q.program_id ? `program_id=${encodeURIComponent(q.program_id)}` : `q=${encodeURIComponent(q.q || '')}`}`);
export const previewGovWatch = (d: { keys: string[]; group_ids: string[]; target_year: number | null }) => req<GovWatchPreview[]>('POST', '/gov-watch/preview', d);
export const createGovWatch = (d: GovWatchInput & { company_id: string }) => req<GovWatch>('POST', '/gov-watch', d);
export const updateGovWatch = (id: string, d: Partial<GovWatchInput> & { status?: 'on' | 'off' }) => req<GovWatch>('PUT', `/gov-watch/${id}`, d);
export const doneGovWatch = (id: string, taskId?: string) => req<{ id: string; status: string }>('POST', `/gov-watch/${id}/done`, { task_id: taskId || '' });
export const seenGovWatch = (id: string) => req<{ seen: number }>('POST', `/gov-watch/${id}/seen`, {});
export const deleteGovWatch = (id: string) => req<{ deleted: number }>('DELETE', `/gov-watch/${id}`);

export const getGovMatchQuality = () => req<{ total: number; missing: Record<string, number> }>('GET', '/gov-match/quality');

// ── AI Presets ──
export const getAiPresets = () => req<AIPreset[]>('GET', '/ai-presets');
export const createAiPreset = (d: Partial<AIPreset>) => req<AIPreset>('POST', '/ai-presets', d);
export const updateAiPreset = (id: string, d: Partial<AIPreset>) =>
  req<AIPreset>('PUT', `/ai-presets/${id}`, d);
export const deleteAiPreset = (id: string) => req<void>('DELETE', `/ai-presets/${id}`);

// ── Categories & Templates ──
export const getTaskCategories = () => req<TaskCategory[]>('GET', '/task-categories');
export const createTaskCategory = (d: Partial<TaskCategory>) => req<TaskCategory>('POST', '/task-categories', d);
export const updateTaskCategory = (id: string, d: Partial<TaskCategory>) =>
  req<TaskCategory>('PUT', `/task-categories/${id}`, d);
export const deleteTaskCategory = (id: string) => req<{ deleted: boolean }>('DELETE', `/task-categories/${id}`);

export const getTaskTemplates = () => req<TaskTemplate[]>('GET', '/task-templates');
export const createTaskTemplate = (d: Partial<TaskTemplate>) => req<TaskTemplate>('POST', '/task-templates', d);
export const updateTaskTemplate = (id: string, d: Partial<TaskTemplate>) =>
  req<TaskTemplate>('PUT', `/task-templates/${id}`, d);
export const deleteTaskTemplate = (id: string) => req<{ deleted: boolean }>('DELETE', `/task-templates/${id}`);

// ── FP Portal ──
export const getFpPortal = (token: string) => req<{
  fp: { id: string; name: string; company: string; phone: string; portal_token: string; color?: string | null };
  companies: Company[];
  tasks: Task[];
  consultations: Consultation[];
  comments: Comment[];
}>('GET', `/fp-portal/${token}`);

// ── Collect 연동 ──
export const createCollectProject = (d: {
  company_id: string; task_id: string; step_id: string;
  collect_ids: string[]; custom_docs: string[];
}) => req<{ project_id: string; items_count: number }>('POST', '/collect/create-project', d);

// ── 팝빌 사업자번호 조회 ──
export interface BizLookupResult {
  biz_no: string; company_reg_num: string; name: string; ceo: string; biz_class: string; biz_type_detail: string;
  corp_scale: string; person_corp: string; address: string;
  established_date: string; established_year: number | null;
  industry_code: string; close_state: string; close_date: string;
  finance: FinanceData[];
}
export const bizLookup = (bizNo: string) => req<BizLookupResult>('GET', `/biz-lookup/${bizNo.replace(/[^0-9]/g, '')}`);

// ── 공휴일 ──
export interface Holiday { id: string; date: string; name: string; type: string }
export const getHolidays = (year: number) => req<Holiday[]>('GET', `/holidays?year=${year}`);
export const addHoliday = (d: { date: string; name: string; type?: string }) => req<Holiday>('POST', '/holidays', d);
export const deleteHoliday = (id: string) => req<{ deleted: boolean }>('DELETE', `/holidays/${id}`);
export const syncHolidays = (year: number) => req<{ synced: number; year: number }>('POST', '/holidays/sync', { year });

// ── 기업 재무정보 (사업자번호 10자리 또는 법인등록번호 13자리) ──
export interface FinanceData {
  year: string; crno: string; fnclDcd?: string; fnclDcdNm?: string;
  revenue: number; totalAssets: number; totalDebt: number; equity: number;
  currentAssets?: number; currentLiabilities?: number;
  operatingProfit?: number; netIncome?: number;
}
export const getCompanyFinance = (key: string) =>
  req<FinanceData[]>('GET', '/company-finance/' + key.replace(/[^0-9]/g, ''));

// ── 보험사(소속) 관리 ──
export interface InsuranceCompany {
  id: string; account_id: string; name: string; color: string; sort_order: number;
}
export const getInsuranceCompanies = () => req<InsuranceCompany[]>('GET', '/insurance-companies');
export const createInsuranceCompany = (d: Partial<InsuranceCompany>) => req<InsuranceCompany>('POST', '/insurance-companies', d);
export const updateInsuranceCompany = (id: string, d: Partial<InsuranceCompany>) => req<InsuranceCompany>('PUT', `/insurance-companies/${id}`, d);
export const deleteInsuranceCompany = (id: string) => req<{ deleted: boolean }>('DELETE', `/insurance-companies/${id}`);

// ── SMS 발송 ──
export const sendSms = (d: { to: string; message: string; title?: string; context?: string; company_id?: string | null; reference_id?: string | null }) =>
  req<{ sent: boolean; msg_id?: string | null; error?: string | null; result_code?: string | null }>('POST', '/sms/send', d);

// ── 법인등록번호 일괄 채우기 ──
export interface BatchFillCrnoResult {
  total: number;        // 대상 업체 수 (biz_no 있고 company_reg_num 없는)
  updated: number;      // 성공
  skipped: number;      // 이미 있거나 조회 불가
  failed: number;       // API 오류
  details: { id: string; name: string; biz_no: string; crno: string; status: 'updated' | 'skip' | 'error'; msg?: string }[];
}
export const batchFillCrno = () =>
  req<BatchFillCrnoResult>('POST', '/batch-fill-crno');

// ── 과금 통계 ──
export interface BillingStats {
  sms: { short: number; long: number; shortCost: number; longCost: number };
  popbill: { count: number; cost: number };
  total: number;
}
export const getBillingStats = (year: number, month: number) =>
  req<BillingStats>('GET', `/billing-stats?year=${year}&month=${month}`);

// ── DB 임포트 ──
export interface ImportResult { inserted: number; updated: number; errors: string[] }
export const importFps = (rows: Record<string, string>[]) => req<ImportResult>('POST', '/db/fps/import', { rows });
export const importWms = (rows: Record<string, string>[]) => req<ImportResult>('POST', '/db/wms/import', { rows });
export const importCompanies = (rows: Record<string, string>[]) => req<ImportResult>('POST', '/db/companies/import', { rows });

// ── AI 질문지 생성 ──
export const generateQuestionnaire = (d: {
  files?: { label: string; name: string; s3_key: string }[];
  extraPrompt?: string;
  includeCollectAnswers?: boolean;
  collectProjectId?: string;
  includeCollectFiles?: boolean;
  taskId?: string;
}) => req<{ questions: string }>('POST', '/ai/generate-questionnaire', d);

// ── AI 사업계획서 생성 ──
export const generateBizplan = (d: {
  files?: { label: string; name: string; s3_key: string }[];
  extraPrompt?: string;
}) => req<{ text: string; docx_s3_key: string }>('POST', '/ai/generate-bizplan', d);

// ── S3 파일 다운로드 URL ──
export const getDownloadUrl = (key: string) =>
  req<{ url: string }>('GET', `/download?key=${encodeURIComponent(key)}`);

// ── 자료수집 프로젝트 생성 (신규 — source 포함) ──
export const createCollectProjectFull = (d: {
  name: string; company_id: string; fp_id?: string;
  items: { label: string; type: string; required: boolean; description?: string; section?: string | null; guides?: unknown[]; options?: string[] }[];
  source: string; task_id: string; step_id: string;
  brand?: 'roum' | 'atv' | 'law';   // 고객 화면 로고 (2026-09-11 · law 2026-10-31)
}) => req<{ project_id: string; items_count: number }>('POST', '/collect/create-project', d);

// ── 자료수집 프로젝트 상세 조회 ──
export async function getCollectProject(id: string) {
  const token = getToken();
  const res = await fetch(`/api/projects/${id}`, { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } });
  return res.json();
}

// ── 자료수집 프로젝트 items 싱크 (업무관리 collectConfig → 프로젝트 DB) ──
interface SyncItem { id?: string; label: string; type: string; required: boolean; sensitive?: boolean; description?: string; options?: string[]; guides?: unknown[] }
export async function addProjectItem(projectId: string, item: SyncItem) {
  const token = getToken();
  const fd = new FormData();
  fd.append('label', item.label || '');
  fd.append('type', item.type || 'file');
  fd.append('required', item.required ? 'true' : 'false');
  fd.append('sensitive', item.sensitive ? 'true' : 'false');
  fd.append('desc', item.description || '');
  if (item.options && item.options.length) fd.append('options', JSON.stringify(item.options));
  if (item.guides && item.guides.length) {
    const meta = (item.guides as Record<string, unknown>[]).map(g => ({
      type: g.type || 'link', label: g.label || '', url: g.url || '', fileName: g.fileName || '',
    }));
    fd.append('guides', JSON.stringify(meta));
  }
  const res = await fetch(`/api/projects/${projectId}/items`, {
    method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
  });
  return res.json();
}
export async function updateProjectItem(projectId: string, itemId: string, patch: Record<string, unknown>) {
  const token = getToken();
  const res = await fetch(`/api/projects/${projectId}/items/${itemId}`, {
    method: 'PUT', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return res.json();
}
export async function deleteProjectItem(projectId: string, itemId: string) {
  const token = getToken();
  const res = await fetch(`/api/projects/${projectId}/items/${itemId}`, {
    method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
  });
  return res.json();
}

// ── 자료수집 알림 메시지 생성 ──
export async function generateCollectMessage(projectId: string, type: string) {
  const token = getToken();
  const res = await fetch('/api/notifications/generate-message', {
    method: 'POST', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, type }),
  });
  return res.json();
}

// ── 자료수집 알림 발송 ──
export async function sendCollectNotification(d: { projectId: string; type: string; recipients: { name: string; phone?: string; email?: string; msgType: string }[]; messageSms?: string; messageEmail?: string }) {
  const token = getToken();
  const res = await fetch('/api/notifications/send', {
    method: 'POST', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...d, channel: "sms" }),
  });
  return res.json();
}

// ── 자료수집 파일 승인/반려/취소/삭제 ──
export async function approveCollectFile(uploadId: string) {
  const token = getToken();
  const res = await fetch(`/api/uploads/${uploadId}/approve`, { method: 'PUT', headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}
export async function unapproveCollectFile(uploadId: string) {
  const token = getToken();
  const res = await fetch(`/api/uploads/${uploadId}/unapprove`, { method: 'PUT', headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}
export async function rejectCollectFile(uploadId: string, reason?: string) {
  const token = getToken();
  const res = await fetch(`/api/uploads/${uploadId}/reject`, {
    method: 'PUT', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || '' }),
  });
  return res.json();
}
export async function deleteCollectUpload(uploadId: string) {
  const token = getToken();
  const res = await fetch(`/api/uploads/${uploadId}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}
export async function batchApproveCollect(ids: string[]) {
  const token = getToken();
  const res = await fetch('/api/uploads/batch-approve', {
    method: 'POST', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadIds: ids }),   // 서버는 uploadIds — 「ids」로 보내 400 이 났는데 성공처럼 보였음(2026-10-13 일괄 승인 · 반려가 안 먹던 원인)
  });
  const j = await res.json();
  if (!res.ok || (j && j.success === false)) throw new Error((j && j.error) || '일괄 승인 실패');
  return j;
}
export async function batchRejectCollect(ids: string[], reason: string) {
  const token = getToken();
  const res = await fetch('/api/uploads/batch-reject', {
    method: 'POST', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadIds: ids, reason }),
  });
  const j = await res.json();
  if (!res.ok || (j && j.success === false)) throw new Error((j && j.error) || '일괄 반려 실패');
  return j;
}
export async function getCollectFileDownloadUrl(uploadId: string) {
  const token = getToken();
  const res = await fetch(`/api/uploads/${uploadId}/download`, { headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}
export async function getCollectGuideDownloadUrl(projectId: string, guideId: string) {
  const token = getToken();
  const res = await fetch(`/api/projects/${projectId}/guide/${guideId}/download`, { headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}

// ── 자료수집 템플릿 목록 ──
export async function getCollectTemplates(): Promise<{ id: string; name: string; itemCount: number }[]> {
  const token = getToken();
  const res = await fetch('/api/templates', { headers: { Authorization: 'Bearer ' + token } });
  if (!res.ok) return [];
  const json = await res.json();
  return Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
}

// ── Tag Presets ──
export interface TagPreset { id: string; label: string; category: string; sort_order: number }
export const getTagPresets = () => req<TagPreset[]>('GET', '/tag-presets');
export const createTagPreset = (d: { label: string; category?: string }) => req<TagPreset>('POST', '/tag-presets', d);
export const updateTagPreset = (id: string, d: { label?: string; category?: string; sort_order?: number }) => req<TagPreset>('PUT', '/tag-presets/' + id, d);
export const deleteTagPreset = (id: string) => req<{ deleted: boolean }>('DELETE', '/tag-presets/' + id);

// ── 공용 엔드포인트 (Single Source of Truth) ──
const SHARED_BASE = '/api/shared';

async function sharedReq<T>(method: string, path: string): Promise<{ success: boolean; data: T }> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
  };
  const res = await fetch(SHARED_BASE + path, opts);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `API ${res.status}`);
  return json;
}

export interface SharedCompany {
  id: string; name: string; biz_no: string; ceo: string;
  ceo_phone: string; ceo_email: string; region: string; biz_type: string;
  fps: { id: string; name: string; phone: string; email: string }[];
}
export interface SharedFP {
  id: string; name: string; phone: string; email: string;
  company: string; referrer_id: string | null;
}
export interface SharedWM {
  id: string; name: string; company: string; phone: string; email: string;
}

export const searchSharedCompanies = (q?: string) =>
  sharedReq<SharedCompany[]>('GET', '/companies' + (q ? '?q=' + encodeURIComponent(q) : ''));
export const searchSharedFps = (q?: string) =>
  sharedReq<SharedFP[]>('GET', '/fps' + (q ? '?q=' + encodeURIComponent(q) : ''));
export const searchSharedWms = (q?: string) =>
  sharedReq<SharedWM[]>('GET', '/wms' + (q ? '?q=' + encodeURIComponent(q) : ''));
